import { isAzqpCounty, AZQP_COUNTY_ABBREVS, AZQP_COUNTY_NAMES } from './azqpCountyBoundaries';

export { AZQP_COUNTY_ABBREVS, AZQP_COUNTY_NAMES };

export const AZQP_VALID_BANDS = ['160', '80', '40', '20', '15', '10'];
export const AZQP_VALID_MODES = ['CW', 'Phone'];

export const AZQP_US_STATES = [
  'AL', 'AK', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY',
  'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC',
  'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const AZQP_CA_PROVINCES = [
  'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'
];

export const AZQP_VALID_EXCHANGES = [
  ...AZQP_COUNTY_ABBREVS,
  ...AZQP_US_STATES,
  ...AZQP_CA_PROVINCES
];

export const AZQP_ENTRY_CATEGORY_OPTIONS = [
  { value: 'MOBILE', label: 'Mobile' },
  { value: 'EXPEDITION-MULTI', label: 'Expedition Multi-Op (county-line or single-county)' },
  { value: 'EXPEDITION-SINGLE', label: 'Expedition Single-Op (county-line or single-county)' },
  { value: 'MULTI-UNLIMITED', label: 'Multi-Op Unlimited Transmitters' },
  { value: 'MULTI-ONE', label: 'Multi-Op One Transmitter' },
  { value: 'SINGLE-OP', label: 'Single-Op' }
];

export const AZQP_POWER_OPTIONS = [
  { value: 'HIGH', label: 'High (>100 W)' },
  { value: 'LOW', label: 'Low (≤100 W)' },
  { value: 'QRP', label: 'QRP (≤5 W)' }
];

export const AZQP_MODE_CATEGORY_OPTIONS = [
  { value: 'MIXED', label: 'Mixed (CW + Phone)' },
  { value: 'CW', label: 'CW Only' },
  { value: 'SSB', label: 'Phone Only' }
];

export const AZQP_STATION_CATEGORY_OPTIONS = [
  { value: 'FIXED', label: 'Fixed' },
  { value: 'MOBILE', label: 'Mobile' },
  { value: 'EXPEDITION-SINGLE', label: 'Expedition — Single County' },
  { value: 'EXPEDITION-LINE', label: 'Expedition — County Line' }
];

export const DEFAULT_AZQP_STATION_SETTINGS = {
  callsign: '',
  sentLocation: '',
  isArizonaStation: false,
  entryCategory: 'SINGLE-OP',
  powerLevel: 'LOW',
  modeCategory: 'MIXED',
  stationCategory: 'FIXED',
  defaultSentRst: '59',
  k7aBonusClaimed: false
};

export const AZQP_FORM = {
  rstField: 'classSent',
  rstLabel: 'RST Rcvd',
  rstPlaceholder: '59',
  rstTooltip: 'Signal report received from the other station (e.g. 59).',
  locationLabel: 'Rcvd Exchange',
  locationPlaceholder: 'MCP, WA, ON, or DX',
  locationTooltip: 'Arizona stations send a 3-letter AZ county. Others send a 2-letter US state or Canadian province/territory. DX stations send a DXCC prefix.',
  showCallSignArea: false
};

export const AZQP_PROGRESS_GROUPS = {
  'Arizona Counties': AZQP_COUNTY_ABBREVS,
  'US States': AZQP_US_STATES,
  'Canada': AZQP_CA_PROVINCES
};

export function normalizeAzqpSettings(settings = {}) {
  const merged = {
    ...DEFAULT_AZQP_STATION_SETTINGS,
    ...settings
  };

  merged.callsign = String(merged.callsign || '').trim().toUpperCase();
  merged.sentLocation = String(merged.sentLocation || '').trim().toUpperCase();
  merged.entryCategory = String(merged.entryCategory || 'SINGLE-OP').toUpperCase();
  merged.powerLevel = String(merged.powerLevel || 'LOW').toUpperCase();
  merged.modeCategory = String(merged.modeCategory || 'MIXED').toUpperCase();
  merged.stationCategory = String(merged.stationCategory || 'FIXED').toUpperCase();
  merged.defaultSentRst = String(merged.defaultSentRst || '59').trim();
  merged.isArizonaStation = Boolean(merged.isArizonaStation);
  merged.k7aBonusClaimed = Boolean(merged.k7aBonusClaimed);

  return merged;
}

export function validateRst(value) {
  const normalized = String(value || '').trim();
  return /^[0-9]{2,3}$/.test(normalized);
}

export function validateAzqpExchange(value) {
  const normalized = String(value || '').trim().toUpperCase();
  if (!normalized) {
    return false;
  }

  if (AZQP_VALID_EXCHANGES.includes(normalized)) {
    return true;
  }

  // DX / non-standard prefixes — 1–4 alphanumerics
  return /^[A-Z0-9]{1,4}$/.test(normalized) && !normalized.startsWith('AZ');
}

export function formatAzqpExchange(settings) {
  const normalized = normalizeAzqpSettings(settings);
  if (!normalized.sentLocation) {
    return '';
  }

  if (normalized.isArizonaStation) {
    return `${normalized.defaultSentRst || '59'} ${normalized.sentLocation}`;
  }

  return `${normalized.defaultSentRst || '59'} ${normalized.sentLocation}`;
}

export function calculateAzqpQsoPoints(contacts) {
  return (contacts || [])
    .filter((contact) => contact.deleted !== 'Y')
    .reduce((total, contact) => total + (contact.mode === 'CW' ? 2 : 1), 0);
}

export function calculateAzqpMultipliers(contacts, settings) {
  const active = (contacts || []).filter((contact) => contact.deleted !== 'Y');
  const normalized = normalizeAzqpSettings(settings);
  const byMode = { CW: new Set(), Phone: new Set() };

  active.forEach((contact) => {
    const exchange = String(contact.locationReceived || '').trim().toUpperCase();
    const modeKey = contact.mode === 'CW' ? 'CW' : 'Phone';
    if (!exchange) {
      return;
    }

    if (normalized.isArizonaStation) {
      if (AZQP_US_STATES.includes(exchange) || AZQP_CA_PROVINCES.includes(exchange)) {
        byMode[modeKey].add(exchange);
      } else if (!isAzqpCounty(exchange)) {
        byMode[modeKey].add(exchange);
      }
      return;
    }

    if (isAzqpCounty(exchange)) {
      byMode[modeKey].add(exchange);
    }
  });

  const cw = byMode.CW.size;
  const phone = byMode.Phone.size;

  return {
    cw,
    phone,
    total: cw + phone,
    isArizonaStation: normalized.isArizonaStation
  };
}

export function calculateAzqpScoreBreakdown(contacts) {
  const active = (contacts || []).filter((contact) => contact.deleted !== 'Y');
  const cw = active.filter((contact) => contact.mode === 'CW').length;
  const phone = active.filter((contact) => contact.mode === 'Phone').length;

  return { cw, phone, digital: 0, total: active.length };
}

export function calculateAzqpProjectedScore(contacts, settings) {
  const qsoPoints = calculateAzqpQsoPoints(contacts);
  const multipliers = calculateAzqpMultipliers(contacts, settings);
  const normalized = normalizeAzqpSettings(settings);
  const bonusPoints = normalized.k7aBonusClaimed ? 100 : 0;
  const multipliedScore = qsoPoints * Math.max(multipliers.total, 1);

  return {
    qsoPoints,
    multipliers,
    multipliedScore,
    bonusPoints,
    finalScore: multipliedScore + bonusPoints
  };
}

export function isAzqpDuplicateContact(contacts, callsign, frequency, mode, locationReceived) {
  const normalizedCall = String(callsign || '').trim().toUpperCase();
  const normalizedLocation = String(locationReceived || '').trim().toUpperCase();

  return (contacts || []).some((contact) => {
    if (contact.deleted === 'Y') {
      return false;
    }

    return contact.callsign?.toUpperCase() === normalizedCall
      && contact.frequency === frequency
      && contact.mode === mode
      && String(contact.locationReceived || '').trim().toUpperCase() === normalizedLocation;
  });
}

export function getAzqpExchangeName(code) {
  const normalized = String(code || '').trim().toUpperCase();
  return AZQP_COUNTY_NAMES[normalized] || normalized;
}
